/**
 * Rocket League physics constants, in unreal units (uu), seconds and radians.
 *
 * SOURCING RULE: every constant in this file must carry a comment naming at
 * least one primary source and a confidence tag:
 *   [confirmed]      two or more INDEPENDENT sources agree on the value
 *   [1-src]          only one source states it
 *   [conflict: ...]  sources disagree; the note states both values, which one
 *                    we took, and why
 *   [UNVERIFIED-ESTIMATE]  no source states it; the comment explains the basis
 *
 * Primary sources:
 *   S   = https://www.smish.dev/rocket_league/*  (ball_simulation_1..3,
 *         ground_control, aerial_control)
 *   RS  = RocketSim   (github.com/ZealanL/RocketSim, main)
 *   RU  = RLUtilities (github.com/samuelpmish/RLUtilities) — branch noted where
 *         master and develop disagree
 *   BW  = RLBot wiki. BW5 = wiki.rlbot.org/v5 (current), BW4 = wiki.rlbot.org/v4,
 *         BWL = the deprecated github.com/RLBot/RLBot/wiki pages
 *   RG  = RLGym  (rlgym/rocket_league/common_values.py)
 *   RBS = rl_ball_sym (github.com/VirxEC/rl_ball_sym) — independent Rust port
 *
 * Cite by SYMBOL NAME, not line number: RocketSim's main branch moves and the
 * line numbers in the research pass were not independently verifiable.
 *
 * Full provenance table, including everything we are NOT sure about, is in
 * docs/CONSTANTS_SOURCES.md. Coordinate-system caveats (Rocket League is
 * LEFT-handed, our frame is right-handed) are in docs/PHYSICS_REFERENCE.md §6.
 *
 * PER-CAR GEOMETRY LIVES IN ./car-config. Hitbox, wheel radii, wheel offsets
 * and suspension rest lengths are properties of a BODY, not of the game, and
 * there are seven bodies. The `OCTANE_*` names below are kept — they are what
 * the rest of the tree imports and what every existing test names — but they
 * are now derived from `OCTANE_CONFIG` rather than written out twice, so the
 * default car and the Octane preset cannot drift apart.
 */

import {
  OCTANE_CONFIG,
  OCTANE_INERTIA_PER_MASS,
  type InertiaPerMass,
} from './car-config';

// ---------------------------------------------------------------------------
// Simulation
// ---------------------------------------------------------------------------

/** Rocket League's physics tick rate. Non-negotiable: the constants below are
 *  tuned for this dt and the model is not dt-invariant. [confirmed: RS, RU,
 *  RG `TICKS_PER_SECOND = 120`] */
export const TICK_RATE = 120;
export const DT = 1 / TICK_RATE;

/** Downward acceleration. Applies to the ball and to airborne cars.
 *  [confirmed: RS `GRAVITY_Z = -650`, RU `Game::gravity = {0,0,-650}`, BW, RG] */
export const GRAVITY = 650;

// ---------------------------------------------------------------------------
// Ball
// ---------------------------------------------------------------------------

/** Collision radius of the soccar ball.
 *  [confirmed: RS `BALL_COLLISION_RADIUS_SOCCAR`, RG `BALL_RADIUS`, BW5, RBS
 *   `STANDARD_RADIUS`]
 *  [conflict: BWL (deprecated wiki) says 92.75 — stale, resolved 4-to-1 against.
 *   RU calls 93.15 its `soccar_collision_radius` and uses it for overlap tests;
 *   that 93.15 is the RESTING HEIGHT (see BALL_REST_Z), not the radius. RBS
 *   carries the same 1.905 uu as a separate CONTACT_BREAKING_THRESHOLD, which
 *   reproduces 91.25 + 1.905 = 93.155 as a rest height without inflating the
 *   sphere. We use 91.25 as the geometric radius.] */
export const BALL_RADIUS = 91.25;

/** Height of the ball's centre when it is at rest on the floor. Greater than
 *  the radius because of the arena mesh collision margin — it is NOT a radius.
 *  [confirmed: RS `BALL_REST_Z`, RG `BALL_RESTING_HEIGHT`, BW5 "elevation at
 *   rest", RU `soccar_collision_radius`] */
export const BALL_REST_Z = 93.15;

/** Coefficient of restitution for ball-vs-world bounces.
 *  [confirmed: S `C_R`, RS `BALL_RESTITUTION`, RU `Ball::restitution`, BW "60%"]
 *  Note RS reaches this via a PATCHED Bullet combine rule: against a static
 *  body restitution = max(a, b) = max(0.6, 0.3) rather than stock Bullet's
 *  a*b = 0.18. Our model applies 0.6 directly. */
export const BALL_RESTITUTION = 0.6;

/** Tangential-impulse coefficient in the smish.dev bounce model.
 *  Despite the name this is NOT a friction coefficient: it is the reduced-mass
 *  ratio m_reduced / m for a solid sphere contacting at radius R, i.e.
 *  1 / (1 + R^2 / (0.4 R^2)) = 1/3.5 = 0.2857142857...
 *  [confirmed: S `mu = 0.285` literal; derivation confirmed against RU
 *   `m_reduced = 1/((1/m) + dot(L,L)/I)`]
 *  [conflict: S prints the rounded 0.285, the exact solid-sphere value is
 *   1/3.5 = 0.285714. We keep S's literal 0.285 because the article presents it
 *   as a measured fit and docs/PHYSICS_REFERENCE.md quotes it verbatim; the
 *   0.25 % difference is below any feel threshold. See CONSTANTS_SOURCES.md.] */
export const BALL_FRICTION = 0.285;

/** Friction-saturation scaling: the tangential impulse is capped at
 *  Y * |v_perp| / |s|. This is the actual Coulomb coefficient of the model.
 *  [confirmed: S `Y = 2.0`, RU `Ball::mu = 2.0f`, RS `CARBALL_COLLISION_FRICTION
 *   = 2.0f`] */
export const BALL_FRICTION_Y = 2.0;

/** Spin-coupling coefficient in the angular-velocity update:
 *      w += A * R * cross(delta_v_para, n)
 *  [confirmed: S `A = 0.0003`; verified analytically as A = 2.5 / R^2 =
 *   2.5 / 91.25^2 = 3.0024e-4 for a solid sphere with I = 0.4 m R^2]
 *  CORRECTION vs an earlier reading of the sources: A is NOT a drag constant.
 *  It appears only in the omega update of `bounce()`, which contains no drag
 *  term at all. Air drag is BALL_DRAG below. */
export const BALL_SPIN_COUPLING = 0.0003;

/** [confirmed: RS `BALL_MASS_BT = CAR_MASS_BT / 6`, RU `Ball::m`, BW, RG] */
export const BALL_MASS = 30;

/** Air drag, applied EXPONENTIALLY once per tick: v *= (1 - BALL_DRAG)^dt.
 *  [confirmed: RS `BALL_DRAG = 0.03f` fed to Bullet as m_linearDamping, and
 *   RBS `const DRAG: f32 = 0.03`]
 *  [conflict: RU uses -0.0305 applied additively (v += -0.0305*v*dt). Magnitudes
 *   differ by ~1.7 % and the FORMS differ; they are not interchangeable. We take
 *   RS+RBS 0.03 with the exponential form, which is what Bullet 3.24 actually
 *   does (`m_linearVelocity *= btPow(1 - m_linearDamping, timeStep)`); the
 *   linear form is the BT_USE_OLD_DAMPING_METHOD branch and is NOT compiled.] */
export const BALL_DRAG = 0.03;

/** [confirmed: RS `BALL_MAX_SPEED`, RU develop `Ball::v_max`, BW, RG, RBS]
 *  [conflict: RU MASTER has 4000 — stale, outvoted 4-to-1.] */
export const BALL_MAX_SPEED = 6000;

/** rad/s. [confirmed: RS `BALL_MAX_ANG_SPEED`, RU `Ball::w_max`, BW, RG, RBS] */
export const BALL_MAX_ANGULAR_SPEED = 6.0;

/** Moment of inertia for a uniform solid sphere: (2/5) m R^2.
 *  [confirmed: RU `Ball::I = 0.4*m*radius*radius` (uses `radius` 91.25, NOT
 *   `collision_radius`), RS via btSphereShape::calculateLocalInertia, RBS]
 *  Not used by the bounce model — mass and inertia cancel out of it — but
 *  needed by any explicit car-ball impulse solver. */
export const BALL_INERTIA = 0.4 * BALL_MASS * BALL_RADIUS * BALL_RADIUS;

/** Bullet material friction on the ball rigid body (ball vs world).
 *  [1-src: RS `BALL_FRICTION`] Combined against the static arena with RS's
 *  patched rule friction = min(a, b) = min(0.35, 0.6) = 0.35. This is a
 *  DIFFERENT quantity from BALL_FRICTION above; do not conflate them. */
export const BALL_WORLD_FRICTION = 0.35;

/** [1-src: RS `ARENA_COLLISION_BASE_FRICTION` / `_RESTITUTION`]
 *  These are the STATIC ARENA body's Bullet material properties. They are what
 *  ./car-arena uses for the car-body-vs-arena contact, because they are the
 *  only car-vs-world pair this file carries. RocketSim additionally has
 *  `CARWORLD_COLLISION_FRICTION` / `_RESTITUTION` on the car side, which Bullet
 *  combines with these; that pair has never been transcribed here and is
 *  recorded as a gap rather than guessed at. The restitution agrees either way
 *  (both are 0.3); the friction would be the lower of the two under RS's
 *  patched `min` combine rule, so using 0.6 here makes a wall scrape grippier
 *  than RocketSim's. See docs/CONSTANTS_SOURCES.md. */
export const ARENA_COLLISION_FRICTION = 0.6;
export const ARENA_COLLISION_RESTITUTION = 0.3;

/** Bullet material properties of a CAR-vs-CAR contact. RocketSim does not let
 *  Bullet combine the two bodies' own materials here: its
 *  `_BtCallback_OnCarCarCollision` OVERWRITES the manifold's combined values
 *  with these two constants on every manifold point, so this pair is the whole
 *  story for car-car and neither ARENA_COLLISION_* nor any per-body material
 *  enters into it. Cars are slippery (0.09 against the arena's 0.6) and almost
 *  perfectly inelastic (0.1) — the visible "bounce" between two cars is the
 *  scripted bump impulse below, not restitution.
 *  [1-src: RS `RLConst::CARCAR_COLLISION_FRICTION` / `_RESTITUTION`] */
export const CARCAR_COLLISION_FRICTION = 0.09;
export const CARCAR_COLLISION_RESTITUTION = 0.1;

// ---------------------------------------------------------------------------
// Car — chassis, hitbox, inertia
// ---------------------------------------------------------------------------

/** [confirmed: RS `CAR_MASS_BT`, RU `Car::m`, BW, RG] */
export const CAR_MASS = 180;

/**
 * Octane collision box, FULL extents (x = length, y = width, z = height).
 * [conflict: RS `HITBOX_SIZES[OCTANE]` = {120.507, 86.6994, 38.6591} vs RU
 *  `hitbox_widths * 2` = {118.0074, 84.1994, 36.1590} — exactly 2.5 uu smaller
 *  on every axis. We take RS. The ONLY argument either way is that RS's box
 *  reproduces RU's own hard-coded inertia tensor: (86.6994^2 + 38.6591^2)/12 =
 *  750.94 ~ 751, (120.507^2 + 38.6591^2)/12 = 1334.7 ~ 1334, (120.507^2 +
 *  86.6994^2)/12 = 1836.6 ~ 1836. RU's own box would give 699.75, not 751.
 *  Caveats: RS's justifying code comment is internally inconsistent (it calls
 *  the OTHER values "slightly larger" when RS's are in fact the larger set),
 *  the community/GetLocalCollisionExtent numbers are RU's, and no in-game
 *  measurement exists to adjudicate. This is unresolved — see
 *  docs/CONSTANTS_SOURCES.md "Unresolved".]
 */
export const OCTANE_HITBOX = {
  length: OCTANE_CONFIG.hitboxSize.x,
  width: OCTANE_CONFIG.hitboxSize.y,
  height: OCTANE_CONFIG.hitboxSize.z,
} as const;

/** Hitbox centre offset from the car origin, in the car's local frame. The
 *  centre of mass stays at local (0,0,0).
 *  [conflict: RS {13.8757, 0, 20.755} vs RU {13.97566, 0, 20.75499}. X differs
 *   by 0.1 uu; Z agrees to 4 dp. We take RS to stay consistent with
 *   OCTANE_HITBOX.] */
export const OCTANE_HITBOX_OFFSET: Readonly<{ x: number; y: number; z: number }> =
  OCTANE_CONFIG.hitboxOffset;

/**
 * Car moment of inertia, as I / m (uu^2) about the car's local axes
 * (x = forward/roll, y = left/pitch, z = up/yaw). Multiply by CAR_MASS.
 *
 * THE OCTANE'S. A body's tensor follows its hitbox, so the per-car value comes
 * from `carInertiaPerMass(config)` in ./car-config — which is what ./suspension
 * and ./car-arena read. This name is the Octane's tensor and is kept because it
 * is what the rest of the tree imports; the sourcing note and the conflict with
 * the geometric derivation are on OCTANE_INERTIA_PER_MASS in ./car-config.
 *
 * NOT used by our aerial model, which is the smish.dev torque/damping form and
 * has the inertia folded into its coefficients.
 */
export const CAR_INERTIA_PER_MASS: InertiaPerMass = OCTANE_INERTIA_PER_MASS;

/** Height of the car's origin when resting on the floor.
 *  [confirmed: RS `CAR_SPAWN_REST_Z = 17`, BW per-body table]
 *  [conflict: BW lists per-body rest heights (Hybrid 17.00, Octane 17.01,
 *   Dominus 17.05, Breakout 18.33, Plank 18.65) "which may vary slightly"; RS
 *   uses a flat 17.0 for every body. We follow RS.] */
export const CAR_SPAWN_REST_Z = 17;

/** Height at which a demolished car is respawned. [1-src: RS `CAR_RESPAWN_Z`] */
export const CAR_RESPAWN_Z = 36;

/** Speed at which throttle acceleration reaches zero (max speed without boost).
 *  [confirmed: RU `Drive::max_throttle_speed`, RS curve hits 0 at 1410, BW] */
export const CAR_MAX_THROTTLE_SPEED = 1410;

/** Absolute speed cap, reachable only with boost.
 *  [confirmed: RS `CAR_MAX_SPEED`, RU `Car::v_max`, BW, RG] */
export const CAR_MAX_SPEED = 2300;

/** rad/s cap on car angular velocity.
 *  [confirmed: RS `CAR_MAX_ANG_SPEED`, RU `Car::w_max`, BW, RG] */
export const CAR_MAX_ANGULAR_SPEED = 5.5;

/** RS treats the car as grounded when at least this many of its 4 wheels touch
 *  the world. Note the air-control torque gate is DIFFERENT: torque applies only
 *  at 0 wheels in contact, while air throttle applies at 0, 1 or 2.
 *  [1-src: RS `isOnGround = numWheelsInContact >= 3`] */
export const WHEELS_FOR_GROUNDED = 3;

// ---------------------------------------------------------------------------
// Ground control — driving curves
// ---------------------------------------------------------------------------

/**
 * Forward acceleration from full throttle vs current forward speed (uu/s^2).
 * Input is clamped to [0, 1410]; interpolate linearly, clamp at both ends.
 * [confirmed: RU `Drive::throttle_accel` verbatim, and equivalent to RS's
 *  normalized DRIVE_SPEED_TORQUE_FACTOR_CURVE — the equivalence is now exact,
 *  not hand-wavy: THROTTLE_TORQUE_AMOUNT 72000 x UU_TO_BT(1/50) = 1440 per
 *  wheel, x 4 wheels = 5760, / CAR_MASS 180 = 32 BT/s^2, x BT_TO_UU(50) =
 *  1600 uu/s^2, using btVehicleRL's frictionScale = mass/3 = 60.]
 */
export const THROTTLE_ACCEL_CURVE: readonly [number, number][] = [
  [0, 1600],
  [1400, 160],
  [1410, 0],
];

/** RS's normalized form of the same curve (torque factor, dimensionless).
 *  [confirmed: RS `DRIVE_SPEED_TORQUE_FACTOR_CURVE`] Kept for cross-checking:
 *  factor * 1600 == THROTTLE_ACCEL_CURVE. */
export const DRIVE_SPEED_TORQUE_FACTOR_CURVE: readonly [number, number][] = [
  [0, 1.0],
  [1400, 0.1],
  [1410, 0.0],
];

/** Throttle force is quartered while fewer than WHEELS_FOR_GROUNDED wheels
 *  touch the world. [1-src: RS `if (numWheelsInContact < 3) driveSpeedScale /= 4`] */
export const THROTTLE_AIRBORNE_WHEEL_PENALTY = 0.25;

/**
 * Maximum turning curvature (1/uu) vs speed (uu/s). Input is clamped to
 * [0, 2300]. Turn radius = 1 / curvature; yaw rate = curvature * speed.
 * [confirmed: RU `Drive::max_turning_curvature` verbatim; the BW slope-form
 *  table reproduces 5 of the 6 rows exactly]
 * [conflict at v = 1500: RU hard-codes 0.00138, BW's slope form yields 0.001375
 *  (0.36 % apart). We take RU's literal 0.00138 because it is source code
 *  rather than a fitted description. Also note BW's final segment is defined out
 *  to 2500 uu/s while RU clips the input at 2300 — irrelevant, since the car
 *  cannot exceed 2300.]
 */
export const STEER_CURVATURE_CURVE: readonly [number, number][] = [
  [0, 0.0069],
  [500, 0.00398],
  [1000, 0.00235],
  [1500, 0.00138],
  [1750, 0.0011],
  [2300, 0.00088],
];

/** RS's alternative parameterization: front-wheel steer ANGLE in radians vs
 *  speed, fed to a Bullet raycast vehicle. NOT row-for-row comparable with
 *  STEER_CURVATURE_CURVE (different model, and its last breakpoint is at 3000
 *  uu/s which the car can never reach). Only useful if we ever implement a
 *  wheel model. [1-src: RS `STEER_ANGLE_FROM_SPEED_CURVE`] */
export const STEER_ANGLE_CURVE: readonly [number, number][] = [
  [0, 0.53356],
  [500, 0.3193],
  [1000, 0.18203],
  [1500, 0.1057],
  [1750, 0.08507],
  [3000, 0.03454],
];

/** Steer angle while powersliding, blended in by the analog handbrake value.
 *  ORDER MATTERS: the blend happens BEFORE multiplying by the steer input —
 *    angle = STEER_ANGLE_CURVE(v)
 *    if (handbrake) angle += (POWERSLIDE_STEER_ANGLE_CURVE(v) - angle) * handbrake
 *    angle *= input.steer
 *  Applying the blend after the steer multiply is wrong for any |steer| < 1.
 *  [1-src: RS `POWERSLIDE_STEER_ANGLE_FROM_SPEED_CURVE`] */
export const POWERSLIDE_STEER_ANGLE_CURVE: readonly [number, number][] = [
  [0, 0.39235],
  [2500, 0.1261],
];

/** Deceleration applied when throttle opposes velocity.
 *  [confirmed: RU `Drive::brake_accel = 3500`, BW "-3500 uu/s^2", and derived
 *   exactly from RS: BRAKE_TORQUE_AMOUNT 2625 x 1/50 = 52.5 per wheel, x
 *   frictionScale 60 = 3150, x 4 wheels = 12600, / 180 = 70 BT/s^2, x 50 =
 *   3500.000 uu/s^2. No longer an inference.] */
export const BRAKE_ACCELERATION = 3500;

/** Deceleration applied when |throttle| is below the deadzone.
 *  [confirmed: RU `Drive::coasting_accel = 525`, BW, and RS's
 *   COASTING_BRAKE_FACTOR 0.15 x 3500 = 525 exactly] */
export const COAST_ACCELERATION = 525;

/** RS's form of the same thing: coasting applies this fraction of full brake.
 *  [1-src: RS `COASTING_BRAKE_FACTOR`] */
export const COASTING_BRAKE_FACTOR = 0.15;

/** |throttle| at or below this coasts rather than drives.
 *  [conflict: RS live code `THROTTLE_DEADZONE = 0.001`; S's prose says 0.01;
 *   RU's `throttle_threshold = 0.05` sits inside a `#if 0` block and is dead.
 *   We take RS's 0.001 — it is the only value that is live, executing code.
 *   The practical difference is nil for keyboard input and small for analog.] */
export const THROTTLE_DEADZONE = 0.001;

/** While braking, engine throttle is forced to zero above this speed.
 *  [1-src: RS `BRAKING_NO_THROTTLE_SPEED_THRESH`] */
export const BRAKING_NO_THROTTLE_SPEED_THRESH = 0.01;

/** Below this forward speed, coasting applies the FULL brake instead of the
 *  0.15 factor; also the "moving" test for the sticky force.
 *  [1-src: RS `STOPPING_FORWARD_VEL`. RU's dead code used 10 — do not use.] */
export const STOPPING_FORWARD_VEL = 25;

/** Analog handbrake ramp: 1/5 s to fully engage, 1/2 s to fully release.
 *  [1-src: RS `POWERSLIDE_RISE_RATE` / `POWERSLIDE_FALL_RATE`. This is the only
 *   time constant anywhere in the friction path; there is NO separate friction
 *   ramp-up constant in either simulator.] */
export const POWERSLIDE_RISE_RATE = 5;
export const POWERSLIDE_FALL_RATE = 2;

/** Lateral tyre friction vs slip ratio. [1-src: RS `LAT_FRICTION_CURVE`] */
export const LAT_FRICTION_CURVE: readonly [number, number][] = [
  [0, 1.0],
  [1, 0.2],
];

/** Longitudinal friction factor. RS's `LONG_FRICTION_CURVE` is literally the
 *  EMPTY curve, and an empty LinearPieceCurve returns its defaultOutput of 1.
 *  So longitudinal friction is always 1. [1-src: RS] */
export const LONG_FRICTION_FACTOR = 1.0;

/** Multipliers blended in by the analog handbrake value:
 *    friction *= (CURVE(slip) - 1) * handbrake + 1
 *  [1-src: RS `HANDBRAKE_LAT_FRICTION_FACTOR_CURVE` (a single point, i.e. the
 *   constant 0.1) and `HANDBRAKE_LONG_FRICTION_FACTOR_CURVE`] */
export const HANDBRAKE_LAT_FRICTION_FACTOR = 0.1;
export const HANDBRAKE_LONG_FRICTION_FACTOR_CURVE: readonly [number, number][] = [
  [0, 0.5],
  [1, 0.9],
];

/** Friction scale applied when the contact is NOT sticky (i.e. throttle == 0),
 *  keyed on the contact normal's Z. Flat ground (n.z = 1) keeps full friction;
 *  a wall (n.z = 0) drops to 0.1 — this is why a car with no throttle slides
 *  off walls. [1-src: RS `NON_STICKY_FRICTION_FACTOR_CURVE`] */
export const NON_STICKY_FRICTION_FACTOR_CURVE: readonly [number, number][] = [
  [0, 0.1],
  [0.7075, 0.5],
  [1, 1.0],
];

// ---------------------------------------------------------------------------
// Suspension and wheels (Octane) — only needed for a raycast-wheel model
// ---------------------------------------------------------------------------

/** [1-src: RS namespace `BTVehicle`. RU has NO suspension model at all — its
 *  entire ground-driving path is `#if 0`'d out — so there is no second source
 *  for anything in this block.]
 *
 *  Everything here except the two rest lengths is GLOBAL in RocketSim — the
 *  stiffness, the damping, the travel and the per-wheel force scales live in
 *  the `BTVehicle` namespace and are the same for every body. The rest lengths
 *  are per-body (`FRONT_WHEEL_SUS_REST` / `BACK_WHEEL_SUS_REST`) and the two
 *  fields below are the OCTANE's, kept under these names because the rest of
 *  the tree imports them. A car reads its own from `car.config`. */
export const SUSPENSION = {
  stiffness: 500,
  dampingCompression: 25,
  dampingRelaxation: 40,
  /** uu of travel. RS subtracts this from the rest length before handing it to
   *  Bullet: 38.755 - 12 = 26.755 front, 37.055 - 12 = 25.055 back. */
  maxTravel: 12,
  subtraction: 0.05,
  /** Per-wheel force scales. The odd arithmetic is real source text:
   *  36 - 1/4 and 54 + 1/4 + 1.5/100. */
  forceScaleFront: 36 - 1 / 4,
  forceScaleBack: 54 + 1 / 4 + 1.5 / 100,
  restLengthFront: OCTANE_CONFIG.frontWheels.suspensionRestLength,
  restLengthBack: OCTANE_CONFIG.backWheels.suspensionRestLength,
} as const;

/** [1-src: RS `FRONT_WHEEL_RADS[OCTANE]` / `BACK_WHEEL_RADS[OCTANE]`] */
export const WHEEL_RADIUS_FRONT = OCTANE_CONFIG.frontWheels.radius;
export const WHEEL_RADIUS_BACK = OCTANE_CONFIG.backWheels.radius;

/** Wheel attachment points in the car's local frame, for the +Y wheel of each
 *  pair; mirror Y for the other side.
 *  [1-src: RS `FRONT_WHEELS_OFFSET[OCTANE]` / `BACK_WHEELS_OFFSET[OCTANE]`]
 *  NOTE: RS's y is the car's RIGHT in its own naming, our +Y column is `left`.
 *  See docs/PHYSICS_REFERENCE.md §6 before using these for anything chiral. */
export const WHEEL_OFFSET_FRONT: Readonly<{ x: number; y: number; z: number }> =
  OCTANE_CONFIG.frontWheels.connectionOffset;
export const WHEEL_OFFSET_BACK: Readonly<{ x: number; y: number; z: number }> =
  OCTANE_CONFIG.backWheels.connectionOffset;

/**
 * Sticky force: an acceleration into the contact surface applied whenever at
 * least one wheel touches the world.
 *   scale = 0.5 (base)  + (fullStick ? 1 - |up.z| : 0)
 *   accel = scale * GRAVITY, directed along -up
 *   fullStick = (throttle != 0) || (|forwardSpeed| > STOPPING_FORWARD_VEL)
 * That gives 325 uu/s^2 on the floor and ceiling, and up to 975 uu/s^2 on a
 * vertical wall.
 * [1-src: RS `Car::_UpdateSticky` formula. The 0.5 base and the (1 - |up.z|)
 *  term are RS-only.]
 * [conflict: BW's Jumping-Physics page also quotes 325 uu/s^2, but scopes it to
 *  a ~3-frame window AFTER a jump, not a continuous ground force — a materially
 *  different model that happens to share the magnitude. The 975 wall figure has
 *  NO independent confirmation. Flagged in CONSTANTS_SOURCES.md.]
 */
export const STICKY_FORCE_BASE_SCALE = 0.5;
export const STICKY_FORCE_GROUND_ACCEL = STICKY_FORCE_BASE_SCALE * GRAVITY; // 325
export const STICKY_FORCE_WALL_ACCEL = (STICKY_FORCE_BASE_SCALE + 1) * GRAVITY; // 975

// ---------------------------------------------------------------------------
// Boost
// ---------------------------------------------------------------------------

/** Forward acceleration added while boosting on the ground. Exact value 2975/3.
 *  [confirmed: RS `BOOST_ACCEL_GROUND`, RU `Drive::boost_accel = 991.667`, BW,
 *   RG] */
export const BOOST_ACCELERATION = 2975 / 3;

/** Forward acceleration added while boosting in the air. Exact value 3175/3.
 *  [conflict: RS 1058.333 vs RU `Aerial::boost_accel = 1060.0`. BW's 1058.333
 *   backs RS, so we take RS. Note also RU's master branch adds throttle_accel on
 *   top while boosting (1126.667) and its develop branch does not — which was
 *   intended is unknown and moot, since both disagree with RS.] */
export const BOOST_ACCELERATION_AIR = 3175 / 3;

/** Boost units consumed per second while boosting. Exact value 100/3.
 *  [confirmed: RS `BOOST_USED_PER_SECOND = BOOST_MAX/3`, BW "33.3/s", RG
 *   `BOOST_CONSUMPTION_RATE`]
 *  [conflict: RU says 30.0/s, but RU's own source annotates that line `// ???`.
 *   Outvoted 3-to-1; do not use 30.] */
export const BOOST_CONSUMPTION_RATE = 100 / 3;

/** [1-src: RS `BOOST_MAX` / `BOOST_SPAWN_AMOUNT`] */
export const BOOST_MAX = 100;
export const BOOST_SPAWN_AMOUNT = BOOST_MAX / 3;

/** Once boosting starts it continues for at least this long even if the button
 *  is released — this is why boost cannot be feathered arbitrarily fast.
 *  [1-src: RS `BOOST_MIN_TIME`. RU has no equivalent.] */
export const BOOST_MIN_TIME = 0.1;

/** Rumble-only boost recharge mutator, OFF by default.
 *  [1-src: RS `RECHARGE_BOOST_PER_SECOND` / `RECHARGE_BOOST_DELAY`] */
export const RECHARGE_BOOST_PER_SECOND = 10;
export const RECHARGE_BOOST_DELAY = 0.25;

/** Forward acceleration from throttle while airborne. Exact value 200/3.
 *  [confirmed: RS `THROTTLE_AIR_ACCEL`, RU `Aerial::throttle_accel`, BW]
 *  [conflict: BW's legacy page claims reverse gives only 33.334 (half). Neither
 *   simulator implements that asymmetry — both scale linearly with throttle in
 *   both directions — and the current BW page does not mention it. We scale
 *   linearly. Flagged in CONSTANTS_SOURCES.md.]
 *  Application gate: this force applies whenever fewer than 3 wheels touch the
 *  world (0, 1 or 2), whereas aerial TORQUE applies only at 0 wheels. */
export const THROTTLE_AIR_ACCELERATION = 200 / 3;

// ---------------------------------------------------------------------------
// Supersonic
// ---------------------------------------------------------------------------

/** Speed at or above which the car becomes supersonic (and demolition-capable).
 *  [confirmed: RS `SUPERSONIC_START_SPEED`, BW, RG] */
export const SUPERSONIC_THRESHOLD = 2200;

/**
 * Hysteresis. Once supersonic, the car STAYS supersonic while its speed is at
 * or above SUPERSONIC_MAINTAIN_MIN_SPEED, but only for
 * SUPERSONIC_MAINTAIN_MAX_TIME of accumulated supersonic time; after that it
 * must re-exceed SUPERSONIC_THRESHOLD. Exact logic:
 *   if (supersonic && supersonicTime < 1.0) supersonic = speed >= 2100;
 *   else                                    supersonic = speed >= 2200;
 *   supersonic ? supersonicTime += dt : supersonicTime = 0;
 * NOTE supersonicTime accumulates over the WHOLE supersonic period, not from
 * the moment speed drops below 2200.
 * [1-src: RS `SUPERSONIC_MAINTAIN_MIN_SPEED` / `SUPERSONIC_MAINTAIN_MAX_TIME`
 *  plus Car.cpp branch structure. RU never computes `supersonic` at all — it
 *  copies the flag out of the game packet.]
 */
export const SUPERSONIC_MAINTAIN_MIN_SPEED = SUPERSONIC_THRESHOLD - 100;
export const SUPERSONIC_MAINTAIN_MAX_TIME = 1.0;

// ---------------------------------------------------------------------------
// Jump
// ---------------------------------------------------------------------------

/** Instant delta-v along the car's up axis on the jump press. Exact 875/3.
 *  [confirmed: RS `JUMP_IMMEDIATE_FORCE`, RU `Jump::speed = 291.667`. BW rounds
 *   to 292 — use the exact rational.] */
export const JUMP_IMPULSE = 875 / 3;

/** Sustained acceleration along the car's up axis while jump is held. Exact
 *  4375/3. [confirmed: RS `JUMP_ACCEL`, RU `Jump::acceleration = 1458.3333`] */
export const JUMP_HOLD_ACCELERATION = 4375 / 3;

/** The hold force is applied for at least this long even if the button is
 *  released immediately (3 ticks at 120 Hz), and at most JUMP_MAX_HOLD_TIME.
 *  [confirmed: RS `JUMP_MIN_TIME` / `JUMP_MAX_TIME`, RU `Jump::min_duration` /
 *   `Jump::max_duration`, BW] */
export const JUMP_MIN_HOLD_TIME = 0.025;
export const JUMP_MAX_HOLD_TIME = 0.2;

/** Multiplier on JUMP_HOLD_ACCELERATION during the first JUMP_MIN_HOLD_TIME.
 *  [conflict: THREE-WAY. RS uses 0.62 (a function-local constexpr its own
 *   author marked TODO); RU uses 0.75 AND additionally subtracts 510 uu/s^2
 *   along forward; BW implies 1.0 with no extra term ("3 ticks applying 36.5 =
 *   (1/120)*3*1460"). Delta-v over the first 3 ticks is 22.6 / 27.3 / 36.5
 *   uu/s respectively — a ~60 % spread. We take RS's 0.62 and drop RU's forward
 *   term, because RS is the only source that both simulates and is internally
 *   consistent with the rest of our jump model. This directly sets jump takeoff
 *   height; see CONSTANTS_SOURCES.md "Unresolved".] */
export const JUMP_PRE_MIN_ACCEL_SCALE = 0.62;

/** Grace pad that stops the jump from being reset while the car is still
 *  touching the ground during takeoff: the jump is not reset while
 *  jumpTime < JUMP_MIN_HOLD_TIME + JUMP_RESET_TIME_PAD = 0.05 s.
 *  [1-src: RS `JUMP_RESET_TIME_PAD = 1/40`, whose own comment says "RL does
 *   something similar to this time-pad, but not exactly the same" — i.e.
 *   self-declared approximate.] */
export const JUMP_RESET_TIME_PAD = 1 / 40;

/** Window during which a double jump or dodge is still available, measured from
 *  the END of the first jump. Total wall-clock window is therefore up to
 *  1.25 + 0.2 = 1.45 s with a maximal first-jump hold.
 *  [confirmed: RS `DOUBLEJUMP_MAX_DELAY`, RU develop `Dodge::timeout`, RG]
 *  [conflict: RU MASTER has 1.5. Outvoted 3-to-1; also BW describes the
 *   effective window as "between 1.25 and 1.45 seconds", matching RS's model of
 *   measuring from the end of the jump rather than the start.] */
export const DOUBLE_JUMP_MAX_DELAY = 1.25;

/** The double jump applies the same impulse as the first jump.
 *  [confirmed: RS applies JUMP_IMMEDIATE_FORCE again, RU `Jump::speed`] */
export const DOUBLE_JUMP_IMPULSE = JUMP_IMPULSE;

// ---------------------------------------------------------------------------
// Dodge / flip
// ---------------------------------------------------------------------------

/** A flip is triggered instead of a double jump when
 *  |pitch| + |yaw| + |roll| >= this. It is the SUM of absolute values of all
 *  three axes, not a vector magnitude.
 *  PER BODY in RocketSim's struct, and 0.5 for all seven; this is the Octane's
 *  and ./jump reads `car.config.dodgeDeadzone`.
 *  [confirmed: RS `CarConfig::dodgeDeadzone`, RU `Dodge::input_threshold`] */
export const DODGE_DEADZONE = OCTANE_CONFIG.dodgeDeadzone;

/** After normalizing the dodge direction, each component below this magnitude
 *  is snapped to zero (so near-pure flips stay pure).
 *  [confirmed: RS and RU both apply the 0.1 component deadzone, and both apply
 *   it AFTER computing the dodge torque.] */
export const DODGE_COMPONENT_DEADZONE = 0.1;

/** Base horizontal delta-v of the dodge impulse, before the speed scaling below.
 *  Applied in the car's YAW-FLATTENED frame, so the impulse is purely
 *  horizontal. [confirmed: RS `FLIP_INITIAL_VEL_SCALE`, RU `500.0f * dodge_dir`] */
export const DODGE_IMPULSE = 500;

/**
 * Speed-dependent scaling of the dodge impulse, with s = |forwardSpeed| / 2300:
 *   forward  : dv.x *= 1                       (no speed bonus at all)
 *   sideways : dv.y *= 1 + 0.9 * s             (up to 950 uu/s)
 *   backward : dv.x *= (1 + 1.5 * s) * 16/15   (up to 1333.33 uu/s)
 * [confirmed: RS stores these as 1 + coeff (`FLIP_*_IMPULSE_MAX_SPEED_SCALE` =
 *  1.0 / 1.9 / 2.5 plus `FLIP_BACKWARD_IMPULSE_SCALE_X` = 16/15); RU writes the
 *  same numbers as (1 + 0.9s) and (16/15)(1 + 1.5s). Algebraically identical.]
 * So: a FORWARD flip always adds exactly DODGE_IMPULSE = 500 uu/s along the
 * flattened forward direction, independent of speed. That is the forward-flip
 * speed bonus, and it is why speed-flipping is about the flip cancel rather
 * than about the impulse.
 */
export const DODGE_SPEED_SCALE_FORWARD = 1.0;
export const DODGE_SPEED_SCALE_SIDE = 1.9;
export const DODGE_SPEED_SCALE_BACKWARD = 2.5;
export const DODGE_BACKWARD_EXTRA_X = 16 / 15;

/** Dodge torque magnitudes, applied in the car's local frame:
 *    torque_local = (-dodgeDir.y * 260, dodgeDir.x * 224, 0)
 *  [confirmed: RS `FLIP_TORQUE_X` (left/right) and `FLIP_TORQUE_Y`
 *   (forward/back); RU `Dodge::side_torque` / `Dodge::forward_torque`] */
export const DODGE_TORQUE_SIDE = 260;
export const DODGE_TORQUE_FORWARD = 224;

/** How long the dodge torque is applied.
 *  [confirmed: RS `FLIP_TORQUE_TIME`, RU `Dodge::torque_time`, RG] */
export const DODGE_TORQUE_TIME = 0.65;

/** Declared in RS as `FLIP_TORQUE_MIN_TIME` but referenced NOWHERE in the whole
 *  RocketSim tree (verified repo-wide), absent from RU, BW, RG and smish.dev.
 *  Vestigial — exported only so nobody re-derives it. DO NOT USE.
 *  [1-src, unused] */
export const FLIP_TORQUE_MIN_TIME_UNUSED = 0.41;

/**
 * Flip z-damping — the thing that makes a flip kill upward momentum.
 * From FLIP_Z_DAMP_START into the flip, while flipTime <= DODGE_TORQUE_TIME:
 *   if (vz < 0 || flipTime < FLIP_Z_DAMP_END)
 *     vz *= (1 - FLIP_Z_DAMP)^(dt / (1/120))
 * [confirmed: RS `FLIP_Z_DAMP_120` / `_START` / `_END`, RU `Dodge::z_damping*`
 *  with identical values]
 * [conflict, form only: RS exponentiates by the tick-rate ratio (tick-rate
 *  independent), RU applies a flat 0.35 per step (correct only at 120 Hz). We
 *  use RS's form.]
 */
export const FLIP_Z_DAMP = 0.35;
export const FLIP_Z_DAMP_START = 0.15;
export const FLIP_Z_DAMP_END = 0.21;

/**
 * FLIP CANCEL. Holding pitch with the SAME sign as the flip's forward/back
 * torque component scales that torque toward zero and simultaneously re-enables
 * air control:
 *   if (sign(dodgeTorque.pitch) === sign(input.pitch))
 *     pitchScale = 1 - min(|input.pitch|, 1);   // 0 at full deflection
 * A flip whose torque is exactly zero (a "stall") has air control for its whole
 * duration.
 * [1-src for the mechanic: RS. RU has NO flip cancel at all — MAJOR MODEL
 *  DISAGREEMENT, and RS is the modern/correct one.]
 */
export const FLIP_CANCEL_ENABLED = true;

/** Pitch input is locked out for DODGE_TORQUE_TIME + FLIP_PITCHLOCK_EXTRA_TIME
 *  = 0.95 s from the start of the flip.
 *  [1-src: RS `FLIP_PITCHLOCK_EXTRA_TIME`; RS's own `FLIP_PITCHLOCK_TIME = 1.0`
 *   is declared but referenced nowhere repo-wide, so 0.95 is what is actually
 *   implemented. RU locks pitch for only 0.3 s from the dodge start.] */
export const FLIP_PITCHLOCK_EXTRA_TIME = 0.3;
export const FLIP_PITCHLOCK_TOTAL_TIME = DODGE_TORQUE_TIME + FLIP_PITCHLOCK_EXTRA_TIME;

/**
 * Landing recovery / dodge cooldown after touching down. ZERO.
 * This is a confirmed ABSENCE, not a guess: RocketSim resets hasFlipped /
 * hasDoubleJumped / flipTime / airTime to 0 on landing with no cooldown, and
 * RLUtilities sets dodge_timer and jump_timer to -1 immediately on ground
 * contact. Neither simulator models a landing stun, no community source
 * publishes frame data for one, and the only post-flip restriction anywhere is
 * FLIP_PITCHLOCK_EXTRA_TIME, which is airborne-only.
 * [confirmed absent: RS, RU. Do NOT invent a nonzero value here.]
 */
export const DODGE_COOLDOWN = 0;

// ---------------------------------------------------------------------------
// Aerial control — tau = Theta * (T*u + D * Theta^T * omega)
// ---------------------------------------------------------------------------

/**
 * Torque coefficients, (roll, pitch, yaw), rad/s^2 at full input.
 * [1-src: S aerial_control, confirmed verbatim digit-for-digit by the
 *  verification pass]
 * [conflict: THREE calibrations of the SAME model exist and they differ by
 *  1-6 %:
 *    S  (ours)  : roll -36.0796, pitch -12.1460, yaw +8.9196
 *    RU         : {-400, -130, 95} / J=10.5  = -38.0952, -12.3810, +9.0476
 *    RS / BW    : {400, 130, 95} * CAR_TORQUE_SCALE = 38.3495, 12.4636, 9.1080
 *  RS's calibration is the best-sourced (BW publishes exactly 38.34/12.46/9.11).
 *  We keep smish.dev's because docs/PHYSICS_REFERENCE.md pins the whole aerial
 *  model to that article and src/physics/aerial.ts is a transcription of it —
 *  mixing a torque calibration from one source with a damping rule from another
 *  is worse than being consistently 5 % off on roll. The RS calibration is
 *  exported below; switching is a one-line change. This is the single largest
 *  open fidelity question in the file — see CONSTANTS_SOURCES.md.]
 */
export const AERIAL_TORQUE = {
  roll: -36.07956616966136,
  pitch: -12.1459978190807,
  yaw: 8.91962804287785,
} as const;

/**
 * Damping coefficients, (roll, pitch, yaw). Pitch and yaw damping are scaled by
 * (1 - |input|) so a fully deflected axis has NO damping; ROLL damping is never
 * disabled.
 * [1-src: S aerial_control, confirmed verbatim]
 * [conflict: same three-way split. S roll damping (-4.4717) is ~6 % below both
 *  RU (-4.7619) and RS (-4.7937) — the largest single disagreement in the whole
 *  constant set. BW publishes torque magnitudes but explicitly NOTHING for
 *  damping, so there is no tiebreaker at all.]
 */
export const AERIAL_DAMPING = {
  roll: -4.47166302201591,
  pitch: -2.798194258050845,
  yaw: -1.886491900437232,
} as const;

/** RocketSim's angle-quantization scale: 2*pi / 2^16 * 1000.
 *  [confirmed: RS `CAR_TORQUE_SCALE`, and BW's published torque magnitudes are
 *   exactly Vec(130, 95, 400) * this] */
export const CAR_TORQUE_SCALE = ((2 * Math.PI) / (1 << 16)) * 1000;

/** The alternative RS/BW calibration of AERIAL_TORQUE / AERIAL_DAMPING, in our
 *  (roll, pitch, yaw) order and sign convention. NOT active — see the conflict
 *  note on AERIAL_TORQUE. RS stores these in PITCH-YAW-ROLL order, which is the
 *  single most likely place to introduce a bug when porting. */
export const AERIAL_TORQUE_ROCKETSIM = {
  roll: -400 * CAR_TORQUE_SCALE,
  pitch: -130 * CAR_TORQUE_SCALE,
  yaw: 95 * CAR_TORQUE_SCALE,
} as const;
export const AERIAL_DAMPING_ROCKETSIM = {
  roll: -50 * CAR_TORQUE_SCALE,
  pitch: -30 * CAR_TORQUE_SCALE,
  yaw: -20 * CAR_TORQUE_SCALE,
} as const;

/** There is NO linear or angular air drag on the car in either simulator. The
 *  only linear attenuation is the hard clamp to CAR_MAX_SPEED; the angular
 *  analogue is the AERIAL_DAMPING torque, which is not a drag.
 *  [confirmed absent: RS sets damping on the BALL only; RU applies only gravity,
 *   thrust, jump accel and torque. Do not add a car air-drag constant.] */
export const CAR_AIR_DRAG = 0;

// ---------------------------------------------------------------------------
// Car-ball contact — the "Psyonix impulse" [S ball_simulation_3]
// ---------------------------------------------------------------------------

/**
 * Extra velocity added to the BALL ONLY on car contact (the car receives no
 * reaction — momentum is genuinely not conserved):
 *   dv       = v_ball - v_car,  clamped in magnitude to PSYONIX_IMPULSE_MAX_DELTA_VEL
 *   n        = ball.position - car.position          // CENTRE-TO-CENTRE, not
 *   n.z     *= PSYONIX_IMPULSE_Z_SCALE               // the contact normal
 *   n        = normalize(n - PSYONIX_IMPULSE_FORWARD_SCALE * dot(n, f) * f)
 *   addedVel = n * |dv| * s(|dv|)
 * Note the result is a DELTA-VELOCITY in uu/s: the ball mass has already been
 * divided out. Writing J = m * |dv| * s * n and then dividing by m again is the
 * classic double-count.
 */

/** Vertical bias applied to the contact direction before normalizing.
 *  [confirmed: RS `BALL_CAR_EXTRA_IMPULSE_Z_SCALE`, RU `n2[2] *= 0.35f`, S] */
export const PSYONIX_IMPULSE_Z_SCALE = 0.35;

/** Fraction of the car-forward component REMOVED from the contact direction.
 *  RS stores the complement (`BALL_CAR_EXTRA_IMPULSE_FORWARD_SCALE = 0.65`) and
 *  applies (1 - 0.65) = 0.35; RU and S write 0.35 directly. Our name means "the
 *  fraction removed", so 0.35 is correct here — do not paste RS's 0.65 in.
 *  [confirmed: RS, RU, S] */
export const PSYONIX_IMPULSE_FORWARD_SCALE = 0.35;

/**
 * s(|dv|): scale factor vs relative speed, piecewise-linear, clamped at both
 * ends. [confirmed: RS `BALL_CAR_EXTRA_IMPULSE_FACTOR_CURVE` and RU
 *  `Ball::scale()` — identical tables in both]
 * Use CLAMP-TO-LAST semantics (RS). RU's version has a real bug: its `values`
 * array is declared [6][2] with only 4 rows initialized, so at |dv| == 4600.0
 * exactly it falls out of the loop and returns -1.0, flipping the impulse INTO
 * the car. `piecewiseLinear` in ./math already clamps correctly.
 */
export const PSYONIX_IMPULSE_SCALE_CURVE: readonly [number, number][] = [
  [0, 0.65],
  [500, 0.65],
  [2300, 0.55],
  [4600, 0.3],
];

/** Relative speed is clamped to this before the curve lookup.
 *  [confirmed: RS `BALL_CAR_EXTRA_IMPULSE_MAXDELTAVEL_UU`, RU `fminf(..., 4600)`] */
export const PSYONIX_IMPULSE_MAX_DELTA_VEL = 4600;

/**
 * The added speed |dv| * s(|dv|) is NOT monotonic. On the [2300, 4600] segment
 * s(dv) = 0.8 - dv/9200, so added speed = 0.8*dv - dv^2/9200, a downward
 * parabola peaking at dv = 3680 with s = 0.4, giving 1472 uu/s. At the 4600
 * clamp you get only 1380 uu/s.
 * CORRECTION vs an earlier reading: the peak is at 3680, not at 4600. Hitting
 * the ball with MORE than 3680 uu/s of closing speed makes the psyonix term
 * WEAKER. [derived from the confirmed curve above]
 */
export const PSYONIX_IMPULSE_PEAK_DELTA_VEL = 3680;
export const PSYONIX_IMPULSE_PEAK_ADDED_SPEED = 1472;

/** The extra impulse fires at most once per tick PER CAR, with at least one
 *  tick between applications from the same car.
 *  CORRECTION: this is per-car, not global — RS stores
 *  `tickCountWhenExtraImpulseApplied` inside each Car's BallHitInfo, so in a
 *  two-car pinch BOTH cars apply their full impulse on the same tick.
 *  [1-src: RS. RU models a single collision event and has no guard.] */
export const PSYONIX_IMPULSE_COOLDOWN_TICKS = 1;

/** Restitution and Coulomb friction of the ORDINARY car-ball contact, before
 *  the psyonix term. Perfectly inelastic.
 *  [1-src for restitution: RS `CARBALL_COLLISION_RESTITUTION = 0`]
 *  [confirmed for friction: RS `CARBALL_COLLISION_FRICTION = 2.0`, RU
 *   `Ball::mu = 2.0`, S `Y = 2.0`] */
export const CARBALL_RESTITUTION = 0;
export const CARBALL_FRICTION = 2.0;

// ---------------------------------------------------------------------------
// Demolition and bumps
// ---------------------------------------------------------------------------

/**
 * FRONT-BUMPER BAND. The contact point, in the attacker's local frame and
 * measured from the attacker's ORIGIN, must have x greater than this for the
 * contact to do anything at all beyond ordinary rigid-body contact.
 *
 * IT GATES BUMPS AND DEMOLITIONS ALIKE — one `hitWithBumper` test in
 * RocketSim's `_BtCallback_OnCarCarCollision` decides both, and only then does
 * the supersonic flag pick which of the two happens. The old name
 * `DEMO_MIN_FORWARD_DIST` said "demo" and so read as if a non-demo contact were
 * unconstrained; it is kept below purely as an alias for existing callers.
 *
 * There is NO relative-speed threshold constant anywhere. A demo requires, in
 * order:
 *   1. the attacker is closing on the victim (speedTowards > awaySpeed),
 *   2. contact on the FRONT BUMPER: contactPoint.x > BUMP_MIN_FORWARD_DIST,
 *   3. the attacker's supersonic FLAG is set (not an instantaneous speed test —
 *      so the SUPERSONIC_MAINTAIN hysteresis above is part of the demo rule),
 *   4. opposing teams, unless team demos are enabled.
 * A contact that clears 1 and 2 but fails 3 or 4 is a BUMP.
 * [1-src: RS `RLConst::BUMP_MIN_FORWARD_DIST`, RS Arena.cpp. RU does not model
 *  demolition at all.]
 */
export const BUMP_MIN_FORWARD_DIST = 64.5;

/** Deprecated alias of BUMP_MIN_FORWARD_DIST. The threshold is not
 *  demolition-specific; see above. */
export const DEMO_MIN_FORWARD_DIST = BUMP_MIN_FORWARD_DIST;

/** Seconds before a demolished car respawns.
 *  [confirmed: RS `DEMO_RESPAWN_TIME`, RG `DEMO_RESPAWN_SECONDS`] */
export const DEMO_RESPAWN_TIME = 3.0;

/**
 * Minimum time between two bumps BY THE SAME ATTACKER ON THE SAME VICTIM.
 *
 * It is not a global cooldown and it is not symmetric: RocketSim stores one
 * `carContact { otherCarID, cooldownTimer }` slot on the ATTACKER, so while A is
 * cooling down against B, A may still bump C on the very next tick, and B may
 * bump A back immediately. See `carContact` on ../physics/car-state.
 * [1-src: RS `BUMP_COOLDOWN_TIME`] */
export const BUMP_COOLDOWN_TIME = 0.25;

/** Bump impulse curves, keyed on the attacker's speed TOWARD the victim (uu/s),
 *  giving delta-v in uu/s. The odd 5/6 and 2/6 first points are real source text.
 *  Applied as: velDir * groundOrAirCurve + hitUpDir * upwardCurve, where the
 *  GROUND/AIR choice is made by the VICTIM's ground state, not the attacker's.
 *  [1-src: RS `BUMP_VEL_AMOUNT_GROUND_CURVE` / `_AIR_CURVE` /
 *   `BUMP_UPWARD_VEL_AMOUNT_CURVE`] */
export const BUMP_VEL_GROUND_CURVE: readonly [number, number][] = [
  [0, 5 / 6],
  [1400, 1100],
  [2200, 1530],
];
export const BUMP_VEL_AIR_CURVE: readonly [number, number][] = [
  [0, 5 / 6],
  [1400, 1390],
  [2200, 1945],
];
export const BUMP_UPWARD_VEL_CURVE: readonly [number, number][] = [
  [0, 2 / 6],
  [1400, 278],
  [2200, 417],
];

// ---------------------------------------------------------------------------
// Boost pads — see ./arena-data for the 34 positions
// ---------------------------------------------------------------------------

/** [confirmed: RS `BoostPads::BOOST_AMOUNT_BIG` / `_SMALL`, BW, RG] */
export const BOOST_PAD_BIG_AMOUNT = 100;
export const BOOST_PAD_SMALL_AMOUNT = 12;

/** Seconds before a picked-up pad respawns.
 *  [confirmed: RS `BoostPads::COOLDOWN_BIG` / `_SMALL`, BW, RG] */
export const BOOST_PAD_BIG_RESPAWN = 10;
export const BOOST_PAD_SMALL_RESPAWN = 4;

/**
 * Pickup volumes. TWO shapes are in play:
 *   - CYLINDER, tested against the car's ORIGIN, used normally.
 *   - BOX (square X/Y half-extents, NOT a circle), tested against the car's full
 *     AABB, used only when the same car was the previously-locked car on that pad.
 * The cylinder test is |carPos.z - padPos.z| < cylHeight; the box spans padPos.z
 * to padPos.z + boxHeight.
 * [1-src: RS namespace `BoostPads`, itself credited to a Rocket Science video]
 * [conflict: BW quotes big pads as "height 168, radius 208" and small as
 *  "height 165, radius 144". The RADII agree; the heights do not, and BW gives
 *  no derivation for its measurement convention. We take RS's.]
 */
export const BOOST_PAD_VOLUME = {
  cylHeight: 95,
  cylRadiusBig: 208,
  cylRadiusSmall: 144,
  boxHeight: 64,
  boxRadiusBig: 160,
  boxRadiusSmall: 120,
} as const;

// ---------------------------------------------------------------------------
// Arena — planes only; full geometry and spawns live in ./arena-data
// ---------------------------------------------------------------------------

/** Standard soccar arena half-extents. */
export const ARENA = {
  /** Side walls at x = +/- this. [confirmed: RS `ARENA_EXTENT_X`, RU, BW, RG] */
  halfWidth: 4096,
  /** Back walls / goal-line plane at y = +/- this. Does NOT include the inner
   *  goal. [confirmed: RS `ARENA_EXTENT_Y`, RU, BW, RG] */
  halfLength: 5120,
  /** Floor plane. [confirmed: RS, RU, BW] */
  floor: 0,
  /**
   * Ceiling collision plane.
   * [conflict: RS `ARENA_HEIGHT = 2048` and RU `walls[1] = {(0,0,2048)}` both
   *  place the actual collision plane at 2048; BW and RG both publish 2044. No
   *  source explains the 4 uu difference. We take 2048 because it is the plane
   *  the two simulators actually collide against; 2044 is exported below for
   *  code that has to agree with RLBot-derived bot logic. NOTE: the header
   *  comment in src/physics/math.ts quotes 2044 — that comment predates this
   *  pass and is not authoritative.]
   */
  ceiling: 2048,
  /** The community/RLBot ceiling figure. [confirmed: BW, RG] */
  ceilingRLBot: 2044,
  /** 45-degree corner planes: |x| + |y| = this.
   *  [confirmed: BW, RG `FIELD_BOUNDING_BOX_SIZE`, and verified exactly against
   *   the RU corner mesh] */
  cornerPlaneIntercept: 8064,
  /** Back of the net. [confirmed: RG `BACK_NET_Y`, and RU's goal mesh] */
  backNetY: 6000,
} as const;
